import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowLeft,
  GraduationCap,
  User,
  FileText,
  CheckSquare,
  Clock,
  MessageSquare,
  Upload,
  Loader2,
  Download,
  Plus,
  Calendar,
  Building2,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { admissionsAPI } from '../../services/api';

const STATUS_COLORS = {
  'Assigned': 'bg-blue-100 text-blue-700',
  'Docs Pending': 'bg-amber-100 text-amber-700',
  'Docs Verified': 'bg-cyan-100 text-cyan-700',
  'Submitted': 'bg-indigo-100 text-indigo-700',
  'Under Review': 'bg-purple-100 text-purple-700',
  'Offer Received': 'bg-emerald-100 text-emerald-700',
  'Accepted': 'bg-green-100 text-green-700',
  'Visa Processing': 'bg-orange-100 text-orange-700',
  'Completed': 'bg-green-200 text-green-800',
  'Rejected': 'bg-red-100 text-red-700'
};

export default function AgentAdmissionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const basePath = useRoleBasePath();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Document upload
  const [uploading, setUploading] = useState(false);
  const [docName, setDocName] = useState('');

  // Remark
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);

  // Checklist
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [updatingChecklist, setUpdatingChecklist] = useState(false);

  const fetchApplication = useCallback(async () => {
    try {
      const data = await admissionsAPI.getById(id);
      setApplication(data);
    } catch (err) {
      console.error('Failed to fetch application:', err);
      toast.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    setUpdatingStatus(true);
    try {
      await admissionsAPI.updateStatus(id, { status: selectedStatus });
      toast.success(`Status updated to "${selectedStatus}"`);
      setSelectedStatus('');
      fetchApplication();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      if (docName) formData.append('docName', docName);
      await admissionsAPI.uploadDoc(id, formData);
      toast.success('Document uploaded');
      setDocName('');
      fetchApplication();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddRemark = async () => {
    if (!remarkText.trim()) return;
    setAddingRemark(true);
    try {
      await admissionsAPI.addRemark(id, { text: remarkText.trim() });
      toast.success('Remark added');
      setRemarkText('');
      fetchApplication();
    } catch (err) {
      toast.error('Failed to add remark');
    } finally {
      setAddingRemark(false);
    }
  };

  const handleChecklistToggle = async (index, completed) => {
    setUpdatingChecklist(true);
    try {
      await admissionsAPI.updateChecklist(id, { index, completed });
      fetchApplication();
    } catch (err) {
      toast.error('Failed to update checklist');
    } finally {
      setUpdatingChecklist(false);
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    setUpdatingChecklist(true);
    try {
      await admissionsAPI.updateChecklist(id, { item: newChecklistItem.trim() });
      setNewChecklistItem('');
      fetchApplication();
    } catch (err) {
      toast.error('Failed to add checklist item');
    } finally {
      setUpdatingChecklist(false);
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </AgentLayout>
    );
  }

  if (!application) {
    return (
      <AgentLayout>
        <div className="text-center py-12">
          <h2 className="text-lg font-semibold text-gray-700">Application not found</h2>
          <Button variant="outline" className="mt-4" onClick={() => navigate(`${basePath}/admissions`)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admissions
          </Button>
        </div>
      </AgentLayout>
    );
  }

  const nextStatuses = application.nextStatuses || [];

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/admissions`)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-outfit font-bold text-gray-900 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                {application.universityName}
              </h1>
              <p className="text-gray-500 text-sm">{application.programName} - {application.intake}</p>
            </div>
          </div>
          <Badge className={`text-sm px-3 py-1 ${STATUS_COLORS[application.status] || 'bg-gray-100 text-gray-700'}`}>
            {application.status}
          </Badge>
        </div>

        {/* Status Transition */}
        {nextStatuses.length > 0 && (
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium whitespace-nowrap">Update Status:</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-52">
                  <SelectValue placeholder="Select next status" />
                </SelectTrigger>
                <SelectContent>
                  {nextStatuses.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || updatingStatus}
                className="bg-emerald-600 hover:bg-emerald-700"
                size="sm"
              >
                {updatingStatus && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Update
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="overview" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <User className="w-4 h-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <FileText className="w-4 h-4" /> Documents
              {application.documents?.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">{application.documents.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="checklist" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <CheckSquare className="w-4 h-4" /> Checklist
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <Clock className="w-4 h-4" /> Timeline
            </TabsTrigger>
            <TabsTrigger value="remarks" className="flex items-center gap-1.5 data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4" /> Remarks
              {application.remarks?.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">{application.remarks.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card className="p-6 bg-white border-0 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-4 h-4" /> Student Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name</span>
                      <span className="font-medium">{application.student?.firstName} {application.student?.lastName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email</span>
                      <span className="font-medium">{application.student?.email || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Phone</span>
                      <span className="font-medium">{application.student?.phone || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Country</span>
                      <span className="font-medium">{application.student?.country || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Application Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">University</span>
                      <span className="font-medium">{application.universityName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Program</span>
                      <span className="font-medium">{application.programName}</span>
                    </div>
                    {application.programLevel && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Level</span>
                        <span className="font-medium">{application.programLevel}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Intake</span>
                      <span className="font-medium">{application.intake}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Assigned By</span>
                      <Badge variant="outline" className="capitalize">{application.assignedBy}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created</span>
                      <span className="font-medium">{new Date(application.createdAt).toLocaleDateString()}</span>
                    </div>
                    {application.subStatus && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Sub-status</span>
                        <span className="font-medium">{application.subStatus}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="p-6 bg-white border-0 shadow-sm space-y-4">
              {/* Upload section */}
              <div className="flex items-end gap-3 pb-4 border-b border-gray-100">
                <div className="flex-1">
                  <Label className="text-sm">Document Name (optional)</Label>
                  <Input
                    value={docName}
                    onChange={(e) => setDocName(e.target.value)}
                    placeholder="e.g. Transcript, SOP"
                    className="mt-1"
                  />
                </div>
                <div>
                  <input
                    type="file"
                    id="doc-upload"
                    className="hidden"
                    onChange={handleDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  />
                  <Button
                    onClick={() => document.getElementById('doc-upload').click()}
                    disabled={uploading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload
                  </Button>
                </div>
              </div>

              {/* Document list */}
              {application.documents?.length > 0 ? (
                <div className="space-y-3">
                  {application.documents.map((doc) => (
                    <div key={doc.docId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm text-gray-900">{doc.name}</p>
                          <p className="text-xs text-gray-500">
                            Uploaded by {doc.uploadedByRole} on {new Date(doc.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No documents uploaded yet.</p>
              )}
            </Card>
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist">
            <Card className="p-6 bg-white border-0 shadow-sm space-y-4">
              {/* Add item */}
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <Input
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder="Add checklist item..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                />
                <Button
                  onClick={handleAddChecklistItem}
                  disabled={updatingChecklist || !newChecklistItem.trim()}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {application.checklist?.length > 0 ? (
                <div className="space-y-2">
                  {application.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={(checked) => handleChecklistToggle(idx, checked)}
                        disabled={updatingChecklist}
                      />
                      <span className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {item.item}
                      </span>
                      {item.completedAt && (
                        <span className="text-xs text-gray-400 ml-auto">
                          {new Date(item.completedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No checklist items. Add one above.</p>
              )}
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card className="p-6 bg-white border-0 shadow-sm">
              {application.timeline?.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                  <div className="space-y-4">
                    {[...application.timeline].reverse().map((entry, idx) => (
                      <div key={idx} className="flex gap-4 relative">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center z-10 flex-shrink-0">
                          <Clock className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="text-sm font-medium text-gray-900">{entry.action}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs capitalize">{entry.byRole}</Badge>
                            <span className="text-xs text-gray-400">
                              {new Date(entry.date).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No timeline entries yet.</p>
              )}
            </Card>
          </TabsContent>

          {/* Remarks Tab */}
          <TabsContent value="remarks">
            <Card className="p-6 bg-white border-0 shadow-sm space-y-4">
              {/* Add remark */}
              <div className="pb-4 border-b border-gray-100 space-y-2">
                <Textarea
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  placeholder="Add a remark..."
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddRemark}
                    disabled={addingRemark || !remarkText.trim()}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {addingRemark && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                    Add Remark
                  </Button>
                </div>
              </div>

              {application.remarks?.length > 0 ? (
                <div className="space-y-3">
                  {[...application.remarks].reverse().map((remark, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">{remark.text}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs capitalize">{remark.byRole}</Badge>
                        <span className="text-xs text-gray-400">
                          {new Date(remark.date).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No remarks yet.</p>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AgentLayout>
  );
}
